import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

const PaymentMethodChart = ({ data }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return;

    // Initialize chart
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    // Process data to get cash and card amounts
    const cashData = data.find(item => item.payment_method === 'cash');
    const cardData = data.find(item => item.payment_method === 'card');
    const cashAmount = parseFloat(cashData?.amount || 0);
    const cardAmount = parseFloat(cardData?.amount || 0);

    const option = {
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          return `
            <div style="font-weight: 600; margin-bottom: 8px;">${params.name}</div>
            <div style="display: flex; align-items: center;">
              <span style="display: inline-block; width: 12px; height: 12px; background: ${params.color}; border-radius: 50%; margin-right: 8px;"></span>
              <span style="flex: 1;">Amount:</span>
              <span style="font-weight: 600; margin-left: 16px;">QR ${params.value.toLocaleString()}</span>
            </div>
            <div style="display: flex; align-items: center; margin-top: 4px;">
              <span style="margin-left: 20px; flex: 1;">Percentage:</span>
              <span style="font-weight: 600; margin-left: 16px;">${params.percent}%</span>
            </div>
          `;
        }
      },
      legend: {
        orient: 'horizontal',
        bottom: 10,
        textStyle: {
          color: '#374151'
        }
      },
      graphic: [
        {
          type: 'group',
          right: 20,
          top: 20,
          children: [
            {
              type: 'rect',
              shape: {
                x: 0,
                y: 0,
                width: 120,
                height: 60
              },
              style: {
                fill: 'rgba(255, 255, 255, 0.9)',
                stroke: 'rgba(0, 0, 0, 0.1)',
                strokeWidth: 1,
                shadowColor: 'rgba(0, 0, 0, 0.1)',
                shadowBlur: 4,
                shadowOffsetY: 2
              }
            },
            {
              type: 'text',
              style: {
                text: 'Cash',
                x: 10,
                y: 18,
                fontSize: 12,
                fontWeight: '500',
                fill: '#059669'
              }
            },
            {
              type: 'text',
              style: {
                text: `QR ${cashAmount.toLocaleString()}`,
                x: 110,
                y: 18,
                fontSize: 12,
                fontWeight: '600',
                fill: '#059669',
                textAlign: 'right'
              }
            },
            {
              type: 'text',
              style: {
                text: 'Card',
                x: 10,
                y: 42,
                fontSize: 12,
                fontWeight: '500',
                fill: '#2563eb'
              }
            },
            {
              type: 'text',
              style: {
                text: `QR ${cardAmount.toLocaleString()}`,
                x: 110,
                y: 42,
                fontSize: 12,
                fontWeight: '600',
                fill: '#2563eb',
                textAlign: 'right'
              }
            }
          ]
        }
      ],
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 8,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false,
            position: 'center'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '18',
              fontWeight: 'bold',
              color: '#374151'
            },
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          labelLine: {
            show: false
          },
          data: data.map((item, index) => ({
            value: parseFloat(item.amount || 0),
            name: item.payment_method === 'cash' ? 'Cash' : 'Card',
            itemStyle: {
              color: item.payment_method === 'cash' ? '#059669' : '#2563eb'
            }
          }))
        }
      ]
    };

    chartInstance.current.setOption(option, true);

    // Handle resize
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data]);

  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  return (
    <div 
      ref={chartRef} 
      className="w-full h-full"
      style={{ minHeight: '250px' }}
    />
  );
};

export default PaymentMethodChart;